import React, { useCallback, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import UploadFileRoundedIcon from "@mui/icons-material/UploadFileRounded";
import CEDatePicker from "components/CEDatePicker";
import dayjs from "dayjs";
import { loadExcelJS } from "helpers/loadExcelJS";
import { useStateContext } from "contexts/ContextProvider";
import { useAuth } from "contexts/AuthContext";
import api from "services/api";
import Overlay from "components/Overlay/Overlay";

const SHEET_NAME_REGEX = /^([A-Za-z]{3})\s+(\d{4})$/;

const MONTH_TO_INDEX = {
  jan: 1,
  feb: 2,
  mar: 3,
  apr: 4,
  may: 5,
  jun: 6,
  jul: 7,
  aug: 8,
  sep: 9,
  oct: 10,
  nov: 11,
  dec: 12,
};

const MONTH_INDEX_TO_ABBREV = {
  1: "Jan",
  2: "Feb",
  3: "Mar",
  4: "Apr",
  5: "May",
  6: "Jun",
  7: "Jul",
  8: "Aug",
  9: "Sep",
  10: "Oct",
  11: "Nov",
  12: "Dec",
};

const DATE_HEADER_ALIASES = ["date", "day", "workdate", "entrydate"];
const CLOCK_IN_HEADER_ALIASES = [
  "clockin",
  "clockintime",
  "start",
  "starttime",
  "in",
  "timein",
  "intime",
  "checkin",
  "checkintime",
  "signin",
  "from",
];
const CLOCK_OUT_HEADER_ALIASES = [
  "clockout",
  "clockouttime",
  "end",
  "endtime",
  "out",
  "timeout",
  "outtime",
  "checkout",
  "checkouttime",
  "signout",
  "to",
];

const FIXED_IMPORT_TIMEZONE_OFFSET_MINUTES = 60;
const MIDNIGHT_WRAP_PREVIOUS_MINUTES_THRESHOLD = 12 * 60;
const MIDNIGHT_WRAP_CURRENT_MINUTES_THRESHOLD = 6 * 60;
const normalizeHeaderKey = (value) => {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "")
    .replace(/-/g, "")
    .replace(/_/g, "")
    .replace(/\./g, "")
    .replace(/:/g, "")
    .replace(/\//g, "");
};

const normalizeCellValue = (value) => {
  try {
    if (value === null || value === undefined) {
      return "";
    }

    if (typeof value === "string") {
      return value.trim();
    }

    if (value instanceof Date) {
      return Number.isNaN(value.getTime()) ? "" : value.toISOString();
    }

    if (typeof value === "object") {
      // ExcelJS may return rich/value objects depending on cell type.
      if (typeof value.text === "string") {
        return value.text.trim();
      }

      if (Array.isArray(value.richText)) {
        return value.richText
          .map((part) => (part && typeof part.text === "string" ? part.text : ""))
          .join("")
          .trim();
      }

      if (Object.prototype.hasOwnProperty.call(value, "result")) {
        return normalizeCellValue(value.result);
      }
    }

    return String(value).trim();
  } catch (error) {
    return "";
  }
};

const excelSerialToDate = (serial) => {
  if (!Number.isFinite(serial)) {
    return null;
  }

  const epoch = new Date(Date.UTC(1899, 11, 30));
  const millis = Math.round(serial * 86400000);
  const date = new Date(epoch.getTime() + millis);
  return Number.isNaN(date.getTime()) ? null : date;
};

const parseSheetName = (sheetName) => {
  const match = SHEET_NAME_REGEX.exec(String(sheetName || "").trim());
  if (!match) {
    return null;
  }

  const month = MONTH_TO_INDEX[match[1].toLowerCase()];
  const year = Number.parseInt(match[2], 10);

  if (!month || !Number.isInteger(year)) {
    return null;
  }

  return { month, year, label: `${match[1]} ${year}` };
};

const parseDateFromCell = (cellValue, sheetDateInfo) => {
  if (cellValue instanceof Date && !Number.isNaN(cellValue.getTime())) {
    return {
      year: cellValue.getUTCFullYear(),
      month: cellValue.getUTCMonth() + 1,
      day: cellValue.getUTCDate(),
    };
  }

  if (typeof cellValue === "number") {
    if (sheetDateInfo && cellValue >= 1 && cellValue <= 31) {
      return {
        year: sheetDateInfo.year,
        month: sheetDateInfo.month,
        day: Math.floor(cellValue),
      };
    }

    const numericDate = excelSerialToDate(cellValue);
    if (numericDate) {
      return {
        year: numericDate.getUTCFullYear(),
        month: numericDate.getUTCMonth() + 1,
        day: numericDate.getUTCDate(),
      };
    }
  }

  const normalized = normalizeCellValue(cellValue);
  if (!normalized) {
    return null;
  }

  if (sheetDateInfo && /^\d{1,2}$/.test(normalized)) {
    return {
      year: sheetDateInfo.year,
      month: sheetDateInfo.month,
      day: Number.parseInt(normalized, 10),
    };
  }

  const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(normalized);
  if (iso) {
    return {
      year: Number.parseInt(iso[1], 10),
      month: Number.parseInt(iso[2], 10),
      day: Number.parseInt(iso[3], 10),
    };
  }

  const us = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(normalized);
  if (us) {
    return {
      year: Number.parseInt(us[3], 10),
      month: Number.parseInt(us[1], 10),
      day: Number.parseInt(us[2], 10),
    };
  }

  const dmy = /^(\d{1,2})-(\d{1,2})-(\d{4})$/.exec(normalized);
  if (dmy) {
    const first = Number.parseInt(dmy[1], 10);
    const second = Number.parseInt(dmy[2], 10);
    const year = Number.parseInt(dmy[3], 10);

    // Prefer DD-MM-YYYY when the first token cannot be a month.
    if (first > 12) {
      return { year, month: second, day: first };
    }

    return { year, month: first, day: second };
  }

  const slashDate = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/.exec(normalized);
  if (slashDate) {
    const first = Number.parseInt(slashDate[1], 10);
    const second = Number.parseInt(slashDate[2], 10);
    let year = Number.parseInt(slashDate[3], 10);
    if (year < 100) {
      year += year >= 70 ? 1900 : 2000;
    }

    // Treat values like 31/05/2026 as DD/MM/YYYY.
    if (first > 12) {
      return { year, month: second, day: first };
    }

    return { year, month: first, day: second };
  }

  const nativeDate = new Date(normalized);
  if (!Number.isNaN(nativeDate.getTime())) {
    return {
      year: nativeDate.getUTCFullYear(),
      month: nativeDate.getUTCMonth() + 1,
      day: nativeDate.getUTCDate(),
    };
  }

  return null;
};

const parseTimeFromCell = (cellValue) => {
  if (cellValue instanceof Date && !Number.isNaN(cellValue.getTime())) {
    return {
      hours: cellValue.getUTCHours(),
      minutes: cellValue.getUTCMinutes(),
    };
  }

  if (typeof cellValue === "number") {
    const numericTime = cellValue >= 1 ? cellValue % 1 : cellValue;
    if (numericTime >= 0 && numericTime < 1) {
      const totalMinutes = Math.round(numericTime * 24 * 60);
      return {
        hours: Math.floor(totalMinutes / 60) % 24,
        minutes: totalMinutes % 60,
      };
    }
  }

  const normalized = normalizeCellValue(cellValue);
  if (!normalized) {
    return null;
  }

  const parseTimeMatch = (match) => {
    if (!match) {
      return null;
    }

    let hours = Number.parseInt(match[1], 10);
    const minutes = Number.parseInt(match[2], 10);
    const meridiem = match[3] ? match[3].toLowerCase() : null;

    if (meridiem) {
      if (hours < 1 || hours > 12) {
        return null;
      }
      if (meridiem === "am") {
        hours = hours === 12 ? 0 : hours;
      } else {
        hours = hours === 12 ? 12 : hours + 12;
      }
    }

    if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) {
      return null;
    }

    return { hours, minutes };
  };

  const directTimeMatch = /^(\d{1,2})[:h.](\d{2})(?::\d{2})?(?:\s*([aApP][mM]))?$/.exec(normalized);
  const parsedDirect = parseTimeMatch(directTimeMatch);
  if (parsedDirect) {
    return parsedDirect;
  }

  // Some files store clock values as date-time text (for example: "11/05/2026 08:30").
  const embeddedTimeMatch = /(?:^|\s)(\d{1,2})[:h.](\d{2})(?::\d{2})?(?:\s*([aApP][mM]))?(?:\s|$)/.exec(normalized);
  const parsedEmbedded = parseTimeMatch(embeddedTimeMatch);
  if (parsedEmbedded) {
    return parsedEmbedded;
  }

  const hourOnlyMatch = /^(\d{1,2})\s*([aApP][mM])$/.exec(normalized);
  if (hourOnlyMatch) {
    const parsedHourOnly = parseTimeMatch([hourOnlyMatch[0], hourOnlyMatch[1], "00", hourOnlyMatch[2]]);
    if (parsedHourOnly) {
      return parsedHourOnly;
    }
  }

  // Last resort for locale-formatted date-time strings.
  const nativeDate = new Date(normalized);
  if (!Number.isNaN(nativeDate.getTime()) && (normalized.includes(":") || /[aApP][mM]/.test(normalized))) {
    return {
      hours: nativeDate.getHours(),
      minutes: nativeDate.getMinutes(),
    };
  }

  if (/[^\d\s]/.test(normalized)) {
    return null;
  }

  const minutesFromMidnight = Number.parseInt(normalized, 10);
  if (!Number.isInteger(minutesFromMidnight) || minutesFromMidnight < 0 || minutesFromMidnight >= 24 * 60) {
    return null;
  }

  return {
    hours: Math.floor(minutesFromMidnight / 60),
    minutes: minutesFromMidnight % 60,
  };
};

const addDaysToDatePart = (datePart, daysToAdd) => {
  if (!datePart || !Number.isInteger(daysToAdd)) {
    return null;
  }

  const utcDate = new Date(Date.UTC(datePart.year, datePart.month - 1, datePart.day + daysToAdd, 0, 0, 0, 0));
  if (Number.isNaN(utcDate.getTime())) {
    return null;
  }

  return {
    year: utcDate.getUTCFullYear(),
    month: utcDate.getUTCMonth() + 1,
    day: utcDate.getUTCDate(),
  };
};

const buildIsoFromParts = (datePart, timePart) => {
  if (!datePart || !timePart) {
    return null;
  }

  const utcMillis = Date.UTC(
    datePart.year,
    datePart.month - 1,
    datePart.day,
    timePart.hours,
    timePart.minutes,
    0,
    0
  ) - (FIXED_IMPORT_TIMEZONE_OFFSET_MINUTES * 60000);

  const date = new Date(utcMillis);

  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date.toISOString();
};

const rangesOverlap = (leftStartIso, leftEndIso, rightStartIso, rightEndIso) => {
  const leftStart = new Date(leftStartIso);
  const leftEnd = new Date(leftEndIso);
  const rightStart = new Date(rightStartIso);
  const rightEnd = new Date(rightEndIso);

  if (
    Number.isNaN(leftStart.getTime())
    || Number.isNaN(leftEnd.getTime())
    || Number.isNaN(rightStart.getTime())
    || Number.isNaN(rightEnd.getTime())
  ) {
    return false;
  }

  return leftStart.getTime() < rightEnd.getTime() && rightStart.getTime() < leftEnd.getTime();
};

const formatDateTimeForIssue = (isoValue) => {
  const value = new Date(isoValue);
  if (Number.isNaN(value.getTime())) {
    return "invalid";
  }

  const fixedOffsetMillis = value.getTime() + (FIXED_IMPORT_TIMEZONE_OFFSET_MINUTES * 60000);
  const fixedOffsetDate = new Date(fixedOffsetMillis);

  const year = fixedOffsetDate.getUTCFullYear();
  const month = String(fixedOffsetDate.getUTCMonth() + 1).padStart(2, "0");
  const day = String(fixedOffsetDate.getUTCDate()).padStart(2, "0");
  const hours = String(fixedOffsetDate.getUTCHours()).padStart(2, "0");
  const minutes = String(fixedOffsetDate.getUTCMinutes()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const formatRangeForIssue = (clockInAtIso, clockOutAtIso) => {
  return `${formatDateTimeForIssue(clockInAtIso)} -> ${formatDateTimeForIssue(clockOutAtIso)}`;
};

const formatDatePartAsIso = (datePart) => {
  if (!datePart || !Number.isInteger(datePart.year) || !Number.isInteger(datePart.month) || !Number.isInteger(datePart.day)) {
    return "";
  }

  const month = String(datePart.month).padStart(2, "0");
  const day = String(datePart.day).padStart(2, "0");
  return `${datePart.year}-${month}-${day}`;
};

const isoDateToComparable = (isoDate) => {
  const normalized = String(isoDate || "").trim();
  if (!/^\d{4}-\d{2}-\d{2}$/.test(normalized)) {
    return null;
  }

  const numeric = Number.parseInt(normalized.replace(/-/g, ""), 10);
  return Number.isFinite(numeric) ? numeric : null;
};

const buildRateRange = (startDate, endDate, rate) => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  startDate,
  endDate,
  rate,
});

const toMonthStartIso = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.startOf("month").format("YYYY-MM-DD") : "";
};

const toMonthEndIso = (value) => {
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.endOf("month").format("YYYY-MM-DD") : "";
};

const normalizeAndChainRateRanges = (ranges) => {
  if (!Array.isArray(ranges) || ranges.length === 0) {
    return [];
  }

  const normalized = ranges.map((range) => {
    const normalizedStart = toMonthStartIso(range.startDate || range.endDate);
    let normalizedEnd = toMonthEndIso(range.endDate || range.startDate);

    if (normalizedStart && normalizedEnd && dayjs(normalizedEnd).isBefore(dayjs(normalizedStart), "day")) {
      normalizedEnd = toMonthEndIso(normalizedStart);
    }

    return {
      ...range,
      startDate: normalizedStart,
      endDate: normalizedEnd,
    };
  });

  for (let index = 1; index < normalized.length; index += 1) {
    const previousEnd = normalized[index - 1]?.endDate;
    const previousEndDate = dayjs(previousEnd);
    if (!previousEndDate.isValid()) {
      continue;
    }

    const nextStart = previousEndDate.add(1, "month").startOf("month").format("YYYY-MM-DD");
    normalized[index].startDate = nextStart;

    const currentEnd = dayjs(normalized[index].endDate);
    if (!currentEnd.isValid() || currentEnd.isBefore(dayjs(nextStart), "day")) {
      normalized[index].endDate = toMonthEndIso(nextStart);
    }
  }

  return normalized;
};

const rowHasAnyData = (row) => {
  const values = row?.values || [];
  for (let index = 1; index < values.length; index += 1) {
    if (normalizeCellValue(values[index])) {
      return true;
    }
  }
  return false;
};

const buildHeaderMap = (row) => {
  const map = {};
  const values = row?.values || [];

  for (let index = 1; index < values.length; index += 1) {
    const normalized = normalizeHeaderKey(values[index]);
    if (normalized) {
      map[normalized] = index;
    }
  }

  return map;
};

const getCellValue = (row, headerMap, aliases, fallbackIndex) => {
  const getCellPreferredValue = (cell) => {
    const rawValue = cell?.value;

    // Prefer raw value for date/time and numeric cells to avoid locale/display-text shifts.
    if (rawValue instanceof Date || typeof rawValue === "number") {
      return rawValue;
    }

    if (rawValue !== null && rawValue !== undefined && typeof rawValue !== "object") {
      const normalizedRawValue = normalizeCellValue(rawValue);
      if (normalizedRawValue !== "") {
        return rawValue;
      }
    }

    const textValue = normalizeCellValue(cell?.text);
    if (textValue !== "") {
      return textValue;
    }

    if (rawValue && typeof rawValue === "object") {
      if (Object.prototype.hasOwnProperty.call(rawValue, "result")) {
        return rawValue.result;
      }

      if (typeof rawValue.text === "string" && rawValue.text.trim() !== "") {
        return rawValue.text.trim();
      }
    }

    return rawValue ?? null;
  };

  if (headerMap) {
    for (const alias of aliases) {
      const colIndex = headerMap[alias];
      if (colIndex) {
        const value = getCellPreferredValue(row.getCell(colIndex));
        if (value !== null && value !== undefined && normalizeCellValue(value) !== "") {
          return value;
        }
      }
    }
  }

  if (fallbackIndex) {
    return getCellPreferredValue(row.getCell(fallbackIndex));
  }

  return null;
};

const TimesheetsImport = () => {
  const theme = useTheme();
  const { currentUser } = useAuth();
  const { setOverlayName, setOverlayData, showSnackbar } = useStateContext();

  const [selectedFileName, setSelectedFileName] = useState("");
  const [parsedRows, setParsedRows] = useState([]);
  const [, setMonthlyPreview] = useState([]);
  const [parseIssues, setParseIssues] = useState([]);
  const [parseDetails, setParseDetails] = useState(null);
  const [importIssues, setImportIssues] = useState([]);
  const [rateRanges, setRateRanges] = useState([]);
  const [isParsing, setIsParsing] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [, setImportStartTime] = useState(null);
  const [summary, setSummary] = useState(null);

  const closeOverlay = useCallback(() => {
    setOverlayName("");
    setOverlayData(undefined);
    setParsedRows([]);
    setMonthlyPreview([]);
    setParseIssues([]);
    setParseDetails(null);
    setImportIssues([]);
    setRateRanges([]);
    setSummary(null);
    setImportProgress(null);
    setImportStartTime(null);
    setSelectedFileName("");
  }, [setOverlayData, setOverlayName]);

  const numericHourlyRate = useMemo(() => {
    const parsed = Number(currentUser?.HourlyRate ?? currentUser?.hourlyRate ?? 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  }, [currentUser?.HourlyRate, currentUser?.hourlyRate]);

  const resolvedImportData = useMemo(() => {
    const preparedRanges = rateRanges
      .map((range) => {
        const startComparable = isoDateToComparable(range.startDate);
        const endComparable = isoDateToComparable(range.endDate);
        const parsedRate = Number(range.rate);

        if (!Number.isFinite(parsedRate) || parsedRate < 0 || !startComparable || !endComparable || endComparable < startComparable) {
          return null;
        }

        return {
          ...range,
          startComparable,
          endComparable,
          parsedRate,
        };
      })
      .filter(Boolean)
      .sort((left, right) => right.startComparable - left.startComparable);

    const rowsWithRates = [];
    const uncoveredRows = [];
    const rangeLimitIssues = [];

    const importedDateComparables = parsedRows
      .map((row) => formatDatePartAsIso(row.clockInDatePart))
      .map((isoDate) => isoDateToComparable(isoDate))
      .filter((value) => Number.isFinite(value));

    const maxImportedComparable = importedDateComparables.length > 0 ? Math.max(...importedDateComparables) : null;
    const maxImportedIso = maxImportedComparable
      ? `${String(maxImportedComparable).slice(0, 4)}-${String(maxImportedComparable).slice(4, 6)}-${String(maxImportedComparable).slice(6, 8)}`
      : "";
    const lastImportedMonthEndComparable = maxImportedIso
      ? isoDateToComparable(dayjs(maxImportedIso).endOf("month").format("YYYY-MM-DD"))
      : null;

    if (Number.isFinite(lastImportedMonthEndComparable)) {
      preparedRanges.forEach((range, index) => {
        if (range.startComparable > lastImportedMonthEndComparable) {
          rangeLimitIssues.push(
            `Rate range ${index + 1}: start month (${range.startDate}) is after the last imported month.`
          );
        }

        if (range.endComparable > lastImportedMonthEndComparable) {
          rangeLimitIssues.push(
            `Rate range ${index + 1}: end month (${range.endDate}) is after the last imported month.`
          );
        }
      });
    }

    parsedRows.forEach((row) => {
      const rowDateIso = formatDatePartAsIso(row.clockInDatePart);
      const rowComparable = isoDateToComparable(rowDateIso);

      if (!rowComparable) {
        uncoveredRows.push(`${row.rowLabel}: invalid row date for rate mapping.`);
        return;
      }

      const matchingRange = preparedRanges.find((range) => rowComparable >= range.startComparable && rowComparable <= range.endComparable);
      if (!matchingRange) {
        uncoveredRows.push(`${row.rowLabel}: no hourly rate range configured for ${rowDateIso}.`);
        return;
      }

      rowsWithRates.push({
        ...row,
        hourlyRateSnapshot: matchingRange.parsedRate,
      });
    });

    const monthlyMinutes = {};
    const monthlyAmount = {};

    rowsWithRates.forEach((row) => {
      const startDate = new Date(row.clockInAt);
      const endDate = new Date(row.clockOutAt);

      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        return;
      }

      const minutes = Math.floor((endDate.getTime() - startDate.getTime()) / 60000);
      if (minutes <= 0) {
        return;
      }

      const previewMonthNumber = row.clockInDatePart?.month;
      const previewYearNumber = row.clockInDatePart?.year;
      const previewMonthLabel = `${MONTH_INDEX_TO_ABBREV[previewMonthNumber] || "Mon"} ${previewYearNumber || 1970}`;

      monthlyMinutes[previewMonthLabel] = (monthlyMinutes[previewMonthLabel] || 0) + minutes;
      monthlyAmount[previewMonthLabel] = (monthlyAmount[previewMonthLabel] || 0) + ((minutes / 60) * row.hourlyRateSnapshot);
    });

    const monthly = Object.entries(monthlyMinutes)
      .map(([monthLabel, totalMinutes]) => {
        const [monthText, yearText] = monthLabel.split(" ");
        const monthIndex = MONTH_TO_INDEX[String(monthText).toLowerCase()] || 1;
        const year = Number.parseInt(yearText, 10) || 1970;

        const hours = Math.floor(totalMinutes / 60);
        const minutes = totalMinutes % 60;
        const amount = monthlyAmount[monthLabel] || 0;

        return {
          monthLabel,
          totalMinutes,
          hours,
          minutes,
          amount,
          sortKey: year * 100 + monthIndex,
        };
      })
      .sort((left, right) => left.sortKey - right.sortKey);

    return {
      rowsWithRates,
      uncoveredRows,
      rangeLimitIssues,
      monthly,
      validRangeCount: preparedRanges.length,
    };
  }, [parsedRows, rateRanges]);

  const canImport = useMemo(() => {
    return (
      resolvedImportData.rowsWithRates.length > 0
      && resolvedImportData.uncoveredRows.length === 0
      && resolvedImportData.rangeLimitIssues.length === 0
      && resolvedImportData.validRangeCount > 0
      && !isParsing
      && !isImporting
    );
  }, [isImporting, isParsing, resolvedImportData]);

  const handleRangeStartChange = useCallback((rangeId, newValue) => {
    const normalizedStart = newValue?.isValid?.() ? newValue.startOf("month").format("YYYY-MM-DD") : "";

    setRateRanges((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== rangeId) return item;
        return {
          ...item,
          startDate: normalizedStart,
          endDate: normalizedStart ? toMonthEndIso(item.endDate || normalizedStart) : "",
        };
      });

      return normalizeAndChainRateRanges(updated);
    });
  }, []);

  const handleRangeEndChange = useCallback((rangeId, newValue) => {
    const normalizedEnd = newValue?.isValid?.() ? newValue.endOf("month").format("YYYY-MM-DD") : "";

    setRateRanges((prev) => {
      const updated = prev.map((item) => {
        if (item.id !== rangeId) return item;
        return {
          ...item,
          endDate: normalizedEnd,
        };
      });

      return normalizeAndChainRateRanges(updated);
    });
  }, []);

  const handleFileChange = useCallback(
    async (event) => {
      const file = event.target.files?.[0];
      event.target.value = "";

      setSummary(null);
      setImportIssues([]);
      setParseDetails(null);

      if (!file) {
        return;
      }

      setSelectedFileName(file.name || "");
      setIsParsing(true);
      setParsedRows([]);
      setMonthlyPreview([]);
      setParseIssues([]);
      setParseDetails(null);

      try {
        const ExcelJS = await loadExcelJS();
        const workbook = new ExcelJS.Workbook();
        const buffer = await file.arrayBuffer();
        await workbook.xlsx.load(buffer);

        const issues = [];
        const preparedRows = [];
        const monthlyMinutes = {};
        const sheetSummaries = [];

        const workbookStats = {
          totalSheets: 0,
          totalSheetRows: 0,
          totalRowsScanned: 0,
          totalRowsWithData: 0,
          totalAcceptedRows: 0,
          totalRejectedRows: 0,
          totalDateOnlyRows: 0,
          totalEmptyRows: 0,
          totalEqualClockInOutRows: 0,
          totalInFileOverlapRows: 0,
          totalParserErrors: 0,
        };

        const targetWorksheets = workbook.worksheets;
        workbookStats.totalSheets = targetWorksheets.length;

        if (targetWorksheets.length === 0) {
          issues.push("No worksheets found in the selected file.");
        }

        targetWorksheets.forEach((worksheet) => {
          let currentRowLabel = `${worksheet.name}!1`;
          try {
            const sheetMeta = parseSheetName(worksheet.name);
            const sheetStats = {
              name: worksheet.name,
              totalRows: worksheet.rowCount,
              startRow: 1,
              hasRecognizedHeader: false,
              rowsScanned: 0,
              rowsWithData: 0,
              acceptedRows: 0,
              rejectedRows: 0,
              dateOnlyRows: 0,
              emptyRows: 0,
              equalClockInOutRows: 0,
              parserErrors: 0,
            };

            workbookStats.totalSheetRows += worksheet.rowCount;

            let startRow = 1;
            let currentDatePart = null;
            let currentDateShiftDays = 0;
            let previousClockInMinutesInBlock = null;

            for (let rowNumber = 1; rowNumber <= worksheet.rowCount; rowNumber += 1) {
              currentRowLabel = `${worksheet.name}!${rowNumber}`;
              try {
                const row = worksheet.getRow(rowNumber);
                if (!rowHasAnyData(row)) {
                  continue;
                }

                const headerMap = buildHeaderMap(row);
                const hasHeaderFields = Boolean(
                  DATE_HEADER_ALIASES.some((alias) => headerMap[alias])
                  || CLOCK_IN_HEADER_ALIASES.some((alias) => headerMap[alias])
                  || CLOCK_OUT_HEADER_ALIASES.some((alias) => headerMap[alias])
                );

                if (hasHeaderFields) {
                  startRow = rowNumber + 1;
                  sheetStats.hasRecognizedHeader = true;
                } else {
                  startRow = rowNumber;
                }
                sheetStats.startRow = startRow;
                break;
              } catch (headerRowError) {
                const headerRowErrorMessage = headerRowError?.message || "Unexpected parser error";
                issues.push(`${currentRowLabel}: parser error (${headerRowErrorMessage})`);
                sheetStats.parserErrors += 1;
                workbookStats.totalParserErrors += 1;
                continue;
              }
            }

            for (let rowNumber = startRow; rowNumber <= worksheet.rowCount; rowNumber += 1) {
              const rowLabel = `${worksheet.name}!${rowNumber}`;
              currentRowLabel = rowLabel;

              try {
                const row = worksheet.getRow(rowNumber);
                sheetStats.rowsScanned += 1;
                workbookStats.totalRowsScanned += 1;

                if (!rowHasAnyData(row)) {
                  sheetStats.emptyRows += 1;
                  workbookStats.totalEmptyRows += 1;
                  continue;
                }

                sheetStats.rowsWithData += 1;
                workbookStats.totalRowsWithData += 1;

                // Enforce fixed sheet layout: A=Date, B=ClockIn, C=ClockOut, D=Notes.
                // This avoids ambiguous header mappings when workbooks contain extra time-related columns.
                const dateRaw = getCellValue(row, null, [], 1);
                const clockInRaw = getCellValue(row, null, [], 2);
                const clockOutRaw = getCellValue(row, null, [], 3);

                const hasDateCellValue = normalizeCellValue(dateRaw) !== "";
                if (hasDateCellValue) {
                  const parsedDatePart = parseDateFromCell(dateRaw, sheetMeta);
                  if (!parsedDatePart) {
                    issues.push(`${rowLabel}: invalid Date value.`);
                    sheetStats.rejectedRows += 1;
                    workbookStats.totalRejectedRows += 1;
                    continue;
                  }
                  currentDatePart = parsedDatePart;
                  currentDateShiftDays = 0;
                  previousClockInMinutesInBlock = null;
                }

                const clockInPart = parseTimeFromCell(clockInRaw);
                const clockOutPart = parseTimeFromCell(clockOutRaw);

                // Allow rows that only define the next date block (no timeslot on that row).
                if (!clockInPart && !clockOutPart) {
                  sheetStats.dateOnlyRows += 1;
                  workbookStats.totalDateOnlyRows += 1;
                  continue;
                }

                if (!currentDatePart) {
                  issues.push(`${rowLabel}: missing Date. Add a date on this row or a previous row.`);
                  sheetStats.rejectedRows += 1;
                  workbookStats.totalRejectedRows += 1;
                  continue;
                }

                if (!clockInPart || !clockOutPart) {
                  issues.push(`${rowLabel}: both ClockIn and ClockOut are required.`);
                  sheetStats.rejectedRows += 1;
                  workbookStats.totalRejectedRows += 1;
                  continue;
                }

                const clockInAt = buildIsoFromParts(currentDatePart, clockInPart);
                const clockOutAt = buildIsoFromParts(currentDatePart, clockOutPart);

                if (!clockInAt || !clockOutAt) {
                  issues.push(`${rowLabel}: invalid Date/ClockIn/ClockOut.`);
                  sheetStats.rejectedRows += 1;
                  workbookStats.totalRejectedRows += 1;
                  continue;
                }

                const clockInMinutes = (clockInPart.hours * 60) + clockInPart.minutes;
                const clockOutMinutes = (clockOutPart.hours * 60) + clockOutPart.minutes;

                if (
                  previousClockInMinutesInBlock !== null
                  && clockInMinutes < previousClockInMinutesInBlock
                  && previousClockInMinutesInBlock >= MIDNIGHT_WRAP_PREVIOUS_MINUTES_THRESHOLD
                  && clockInMinutes <= MIDNIGHT_WRAP_CURRENT_MINUTES_THRESHOLD
                ) {
                  currentDateShiftDays += 1;
                }

                const effectiveClockInDatePart = addDaysToDatePart(currentDatePart, currentDateShiftDays);
                if (!effectiveClockInDatePart) {
                  issues.push(`${rowLabel}: invalid ClockIn date after midnight adjustment.`);
                  sheetStats.rejectedRows += 1;
                  workbookStats.totalRejectedRows += 1;
                  continue;
                }

                if (clockOutMinutes === clockInMinutes) {
                  // Skip rows with equal clock in/out times silently (likely incomplete entries).
                  sheetStats.equalClockInOutRows += 1;
                  workbookStats.totalEqualClockInOutRows += 1;
                  previousClockInMinutesInBlock = clockInMinutes;
                  continue;
                }

                const isOvernightShift = clockOutMinutes < clockInMinutes;
                const effectiveClockOutDatePart = isOvernightShift
                  ? addDaysToDatePart(effectiveClockInDatePart, 1)
                  : effectiveClockInDatePart;

                if (!effectiveClockOutDatePart) {
                  issues.push(`${rowLabel}: invalid ClockOut date after overnight adjustment.`);
                  sheetStats.rejectedRows += 1;
                  workbookStats.totalRejectedRows += 1;
                  continue;
                }

                const clockInAtAdjusted = buildIsoFromParts(effectiveClockInDatePart, clockInPart);
                const clockOutAtAdjusted = buildIsoFromParts(effectiveClockOutDatePart, clockOutPart);
                if (!clockInAtAdjusted || !clockOutAtAdjusted) {
                  issues.push(`${rowLabel}: invalid ClockIn/ClockOut value after date adjustment.`);
                  sheetStats.rejectedRows += 1;
                  workbookStats.totalRejectedRows += 1;
                  continue;
                }

                const clockInDate = new Date(clockInAtAdjusted);
                const clockOutDate = new Date(clockOutAtAdjusted);
                const diffMinutes = Math.floor((clockOutDate.getTime() - clockInDate.getTime()) / 60000);
                if (diffMinutes <= 0) {
                  issues.push(`${rowLabel}: duration must be greater than 0.`);
                  sheetStats.rejectedRows += 1;
                  workbookStats.totalRejectedRows += 1;
                  continue;
                }

                preparedRows.push({
                  rowNumber,
                  rowLabel,
                  sheetName: worksheet.name,
                  clockInDatePart: effectiveClockInDatePart,
                  clockInAt: clockInAtAdjusted,
                  clockOutAt: clockOutAtAdjusted,
                  notes: null,
                });

                previousClockInMinutesInBlock = clockInMinutes;

                sheetStats.acceptedRows += 1;
                workbookStats.totalAcceptedRows += 1;
              } catch (rowError) {
                const rowErrorMessage = rowError?.message || "Unexpected parser error";
                issues.push(`${rowLabel}: parser error (${rowErrorMessage})`);
                sheetStats.parserErrors += 1;
                workbookStats.totalParserErrors += 1;
              }
            }

            sheetSummaries.push(sheetStats);
          } catch (sheetError) {
            const sheetErrorMessage = sheetError?.message || "Unexpected parser error";
            issues.push(`${currentRowLabel}: parser error (${sheetErrorMessage})`);
            workbookStats.totalParserErrors += 1;
            sheetSummaries.push({
              name: worksheet.name,
              totalRows: worksheet.rowCount,
              startRow: 1,
              hasRecognizedHeader: false,
              rowsScanned: 0,
              rowsWithData: 0,
              acceptedRows: 0,
              rejectedRows: 0,
              dateOnlyRows: 0,
              emptyRows: 0,
              equalClockInOutRows: 0,
              parserErrors: 1,
            });
          }
        });

        const sheetSummaryByName = sheetSummaries.reduce((acc, sheetSummary) => {
          acc[sheetSummary.name] = sheetSummary;
          return acc;
        }, {});

        const importableRows = [];
        for (const candidateRow of preparedRows) {
          const conflictingRow = importableRows.find((acceptedRow) =>
            rangesOverlap(acceptedRow.clockInAt, acceptedRow.clockOutAt, candidateRow.clockInAt, candidateRow.clockOutAt)
          );

          if (conflictingRow) {
            const candidateRange = formatRangeForIssue(candidateRow.clockInAt, candidateRow.clockOutAt);
            const conflictingRange = formatRangeForIssue(conflictingRow.clockInAt, conflictingRow.clockOutAt);
            issues.push(
              `${candidateRow.rowLabel}: ${candidateRange} overlaps ${conflictingRow.rowLabel}: ${conflictingRange}`
            );
            workbookStats.totalInFileOverlapRows += 1;
            workbookStats.totalRejectedRows += 1;
            if (workbookStats.totalAcceptedRows > 0) {
              workbookStats.totalAcceptedRows -= 1;
            }

            const relatedSheetSummary = sheetSummaryByName[candidateRow.sheetName];
            if (relatedSheetSummary) {
              relatedSheetSummary.rejectedRows += 1;
              if (relatedSheetSummary.acceptedRows > 0) {
                relatedSheetSummary.acceptedRows -= 1;
              }
            }
            continue;
          }

          importableRows.push(candidateRow);

          const startDate = new Date(candidateRow.clockInAt);
          const endDate = new Date(candidateRow.clockOutAt);
          if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
            continue;
          }

          const minutes = Math.floor((endDate.getTime() - startDate.getTime()) / 60000);
          if (minutes <= 0) {
            continue;
          }

          const previewMonthNumber = candidateRow.clockInDatePart?.month;
          const previewYearNumber = candidateRow.clockInDatePart?.year;
          const previewMonthLabel = `${MONTH_INDEX_TO_ABBREV[previewMonthNumber] || "Mon"} ${previewYearNumber || 1970}`;
          monthlyMinutes[previewMonthLabel] = (monthlyMinutes[previewMonthLabel] || 0) + minutes;
        }

        const rowDateValues = importableRows
          .map((row) => formatDatePartAsIso(row.clockInDatePart))
          .map((isoDate) => isoDateToComparable(isoDate))
          .filter((value) => Number.isFinite(value));

        let nextRateRanges = [];
        if (rowDateValues.length > 0) {
          const minDateComparable = Math.min(...rowDateValues);
          const maxDateComparable = Math.max(...rowDateValues);

          const minDateText = String(minDateComparable);
          const maxDateText = String(maxDateComparable);

          const startDate = toMonthStartIso(`${minDateText.slice(0, 4)}-${minDateText.slice(4, 6)}-${minDateText.slice(6, 8)}`);
          const endDate = toMonthEndIso(`${maxDateText.slice(0, 4)}-${maxDateText.slice(4, 6)}-${maxDateText.slice(6, 8)}`);

          nextRateRanges = normalizeAndChainRateRanges([
            buildRateRange(startDate, endDate, String(numericHourlyRate || 0)),
          ]);
        }

        setParsedRows(importableRows);
        setMonthlyPreview([]);
        setRateRanges(nextRateRanges);
        setParseIssues(issues);
        setParseDetails({
          ...workbookStats,
          sheetSummaries,
          issueCount: issues.length,
        });
      } catch (error) {
        setParsedRows([]);
        setMonthlyPreview([]);
        setRateRanges([]);
        setParseDetails(null);
        const detail = error?.message ? ` ${error.message}` : "";
        setParseIssues([`Failed to read XLSX file.${detail}`]);
      } finally {
        setIsParsing(false);
      }
    },
    [numericHourlyRate]
  );

  const handleImport = useCallback(async () => {
    if (!canImport) {
      return;
    }

    if (resolvedImportData.uncoveredRows.length > 0) {
      showSnackbar("Some rows are outside configured rate ranges. Please adjust ranges before import.", theme, "warning");
      return;
    }

    setIsImporting(true);
    setImportIssues([]);
    setImportProgress(null);
    setSummary(null);
    const startTime = Date.now();
    setImportStartTime(startTime);

    try {
      const BATCH_SIZE = 1000;
      const importEntries = resolvedImportData.rowsWithRates.map((row) => ({
        rowNumber: row.rowNumber,
        rowLabel: row.rowLabel,
        clockInAt: row.clockInAt,
        clockOutAt: row.clockOutAt,
        notes: row.notes,
        hourlyRateSnapshot: row.hourlyRateSnapshot,
      }));

      const totalRows = importEntries.length;
      const totalBatches = Math.ceil(totalRows / BATCH_SIZE);

      let totalImportedCount = 0;
      let totalSkippedCount = 0;
      let rowsProcessed = 0;
      const allSkipped = [];

      for (let batchIndex = 0; batchIndex < totalBatches; batchIndex += 1) {
        const batchStart = batchIndex * BATCH_SIZE;
        const batchEnd = Math.min(batchStart + BATCH_SIZE, totalRows);
        const batchRows = importEntries.slice(batchStart, batchEnd);

        try {
          const response = await api.post("/timesheets/import", {
            entries: batchRows,
          });

          const importedCount = Number(response?.data?.importedCount || 0);
          const skippedCount = Number(response?.data?.skippedCount || 0);
          const skipped = Array.isArray(response?.data?.skipped) ? response.data.skipped : [];

          totalImportedCount += importedCount;
          totalSkippedCount += skippedCount;
          rowsProcessed += batchRows.length;
          allSkipped.push(...skipped);

          setImportProgress({
            rowsProcessed,
            totalRows,
            percentComplete: Math.round((rowsProcessed / totalRows) * 100),
          });
        } catch (batchError) {
          const errorMsg = batchError?.response?.data?.error || batchError?.message || "Batch import failed";
          const batchElapsedMs = Date.now() - startTime;
          const batchElapsedSeconds = Math.max(0, Math.round(batchElapsedMs / 1000));
          setSummary({
            error: errorMsg,
            elapsedSeconds: batchElapsedSeconds,
          });
          setIsImporting(false);
          setImportProgress(null);
          setImportStartTime(null);
          return;
        }
      }

      const totalElapsedMs = Date.now() - startTime;
      const elapsedSeconds = Math.max(0, Math.round(totalElapsedMs / 1000));
      setSummary({
        importedCount: totalImportedCount,
        skippedCount: totalSkippedCount,
        totalBatches,
        elapsedSeconds,
      });
      setImportIssues(
        allSkipped.map((item) => `${item.rowLabel || `Row ${item.rowNumber || "?"}`}: ${item.reason || "Skipped by server"}`)
      );
    } catch (error) {
      const errorElapsedMs = Date.now() - startTime;
      const errorElapsedSeconds = Math.max(0, Math.round(errorElapsedMs / 1000));
      setSummary({
        error: error?.response?.data?.error || "Failed to import timesheets",
        elapsedSeconds: errorElapsedSeconds,
      });
    } finally {
      setIsImporting(false);
      setImportProgress(null);
      setImportStartTime(null);
    }
  }, [canImport, resolvedImportData, showSnackbar, theme]);

  // Close/Import moved from the legacy bottom bar into the Overlay footer (R2).
  const footerActions = (
    <Box
      sx={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 1,
        width: "100%",
      }}
    >
      <Button
        onClick={closeOverlay}
        disabled={isImporting}
        variant="outlined"
        sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
      >
        Close
      </Button>
      {!summary && (
        <Button
          variant="contained"
          onClick={handleImport}
          disabled={!canImport}
          sx={{ borderRadius: "8px", textTransform: "none", px: 3 }}
        >
          Import
        </Button>
      )}
    </Box>
  );

  return (
    <Overlay
      id="timesheets-import"
      open={true}
      onClose={closeOverlay}
      title="Timesheet Import"
      maxWidthPx={980}
      backButtonProps={{ disabled: isImporting }}
      footer={footerActions}
    >
      <Box
        sx={{
          p: "1rem 2.3rem 1rem 2.5rem",
          width: "100%",
        }}
      >
          <Grid container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Alert severity="info">
                Upload an XLSX workbook. Sheet names can be anything. Expected columns are
                <strong> Date</strong>, <strong>ClockIn</strong>, <strong>ClockOut</strong> (optional <strong>Notes</strong> in column D).
              </Alert>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Typography variant="body2" color="text.secondary">
                Before import, this will preview monthly totals (hours, minutes, and amount) without writing to DB.
              </Typography>
            </Grid>

            <Grid size={{ xs: 12 }}>
              <Button
                component="label"
                variant="outlined"
                startIcon={<UploadFileRoundedIcon />}
                disabled={isParsing || isImporting}
                sx={{ textTransform: "none", borderRadius: "8px" }}
              >
                Select XLSX Workbook
                <input type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={handleFileChange} />
              </Button>
              {selectedFileName ? (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  Selected: {selectedFileName}
                </Typography>
              ) : null}
            </Grid>

            {(isParsing || isImporting) && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {isParsing ? "Parsing file..." : importProgress ? `Importing batch ${importProgress.currentBatch}/${importProgress.totalBatches}...` : "Importing rows..."}
                  </Typography>
                </Box>
              </Grid>
            )}

            {(isParsing || isImporting) && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    {isParsing ? "Parsing file..." : "Importing rows..."}
                  </Typography>
                </Box>
              </Grid>
            )}

            {isImporting && importProgress && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 3 }}>
                  <CircularProgress
                    variant="determinate"
                    value={importProgress.percentComplete}
                    size={100}
                    thickness={3}
                  />
                  <Typography variant="h6" fontWeight={700}>
                    {importProgress.percentComplete}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Importing {importProgress.rowsProcessed} / {importProgress.totalRows} rows...
                  </Typography>
                </Box>
              </Grid>
            )}

            {summary && (
              <Grid size={{ xs: 12 }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2, py: 4 }}>
                  {summary.error ? (
                    <>
                      <Typography variant="h6" fontWeight={700} color="error">
                        Import Failed
                      </Typography>
                      <Typography variant="body2" sx={{ textAlign: "center" }}>
                        {summary.error}
                      </Typography>
                    </>
                  ) : (
                    <>
                      <Box sx={{ textAlign: "center" }}>
                        <Typography variant="h5" fontWeight={700} color="success.main" sx={{ mb: 1 }}>
                          Import Complete
                        </Typography>
                        <Typography variant="body1" sx={{ mb: 1 }}>
                          Imported <strong>{summary.importedCount}</strong> timesheet rows successfully
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Across <strong>{summary.totalBatches}</strong> batch(es) in <strong>{summary.elapsedSeconds}</strong> second(s)
                        </Typography>
                        {summary.skippedCount > 0 && (
                          <Typography variant="body2" color="warning.main" sx={{ mt: 1 }}>
                            {summary.skippedCount} row(s) were skipped. See details below.
                          </Typography>
                        )}
                      </Box>
                    </>
                  )}
                </Box>
              </Grid>
            )}

            {!isImporting && !summary && parsedRows.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="info">
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                    Hourly Rate Ranges
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 1 }}>
                    Configure one or more date ranges. Each imported row will use the rate of the matching range.
                  </Typography>

                  <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                    {rateRanges.map((range, index) => (
                      <Box
                        key={range.id}
                        sx={{
                          display: "grid",
                          gridTemplateColumns: { xs: "1fr", md: "minmax(140px, 1fr) minmax(140px, 1fr) minmax(120px, 0.7fr) auto" },
                          gap: 1,
                          alignItems: "center",
                        }}
                      >
                        <CEDatePicker
                          label="Start Month"
                          views={["year", "month"]}
                          format="MMM YYYY"
                          value={range.startDate ? dayjs(range.startDate) : null}
                          onChange={(newValue) => handleRangeStartChange(range.id, newValue)}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                            },
                          }}
                        />
                        <CEDatePicker
                          label="End Month"
                          views={["year", "month"]}
                          format="MMM YYYY"
                          value={range.endDate ? dayjs(range.endDate) : null}
                          onChange={(newValue) => handleRangeEndChange(range.id, newValue)}
                          slotProps={{
                            textField: {
                              size: "small",
                              fullWidth: true,
                            },
                          }}
                        />
                        <TextField
                          label="Hourly Rate"
                          type="number"
                          size="small"
                          value={range.rate}
                          onChange={(event) => {
                            const value = event.target.value;
                            setRateRanges((prev) => prev.map((item) => item.id === range.id ? { ...item, rate: value } : item));
                          }}
                          inputProps={{ min: 0, step: "0.01" }}
                        />
                        <Button
                          variant="outlined"
                          color="error"
                          disabled={rateRanges.length <= 1}
                          onClick={() => {
                            setRateRanges((prev) => normalizeAndChainRateRanges(prev.filter((item) => item.id !== range.id)));
                          }}
                          sx={{ textTransform: "none" }}
                        >
                          Remove
                        </Button>
                      </Box>
                    ))}
                  </Box>

                  <Box sx={{ mt: 1 }}>
                    <Button
                      variant="outlined"
                      onClick={() => {
                        const fallbackPreviousEnd = rateRanges[rateRanges.length - 1]?.endDate || rateRanges[rateRanges.length - 1]?.startDate || "";
                        const fallbackStartDate = fallbackPreviousEnd
                          ? dayjs(fallbackPreviousEnd).add(1, "month").startOf("month").format("YYYY-MM-DD")
                          : "";
                        const fallbackEndDate = fallbackStartDate ? toMonthEndIso(fallbackStartDate) : "";
                        setRateRanges((prev) => [
                          ...prev,
                          buildRateRange(fallbackStartDate, fallbackEndDate, String(numericHourlyRate || 0)),
                        ]);
                      }}
                      sx={{ textTransform: "none" }}
                    >
                      Add Rate Range
                    </Button>
                  </Box>
                </Alert>
              </Grid>
            )}

            {!isImporting && !summary && resolvedImportData.uncoveredRows.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Rows Without Rate Coverage ({resolvedImportData.uncoveredRows.length})
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                    Add or adjust rate ranges so every parsed row date is covered.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, my: 0, maxHeight: 200, overflowY: "auto" }}>
                    {resolvedImportData.uncoveredRows.slice(0, 25).map((issue) => (
                      <li key={issue}>
                        <Typography variant="caption">{issue}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}

            {!isImporting && !summary && resolvedImportData.rangeLimitIssues.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="error">
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Rate Range Date Errors ({resolvedImportData.rangeLimitIssues.length})
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                    Range start/end month cannot be after the last month available in the imported file.
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, my: 0, maxHeight: 200, overflowY: "auto" }}>
                    {resolvedImportData.rangeLimitIssues.map((issue) => (
                      <li key={issue}>
                        <Typography variant="caption">{issue}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}

            {!isImporting && !summary && resolvedImportData.monthly.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="success">
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 1 }}>
                    Monthly Preview (not imported yet)
                  </Typography>
                  <Box sx={{ display: "grid", gridTemplateColumns: "minmax(120px, 1fr) minmax(90px, 0.8fr) minmax(90px, 0.8fr) minmax(120px, 1fr)", gap: 1 }}>
                    <Typography variant="caption" fontWeight={800}>Month</Typography>
                    <Typography variant="caption" fontWeight={800}>Hours</Typography>
                    <Typography variant="caption" fontWeight={800}>Minutes</Typography>
                    <Typography variant="caption" fontWeight={800}>Amount</Typography>
                    {resolvedImportData.monthly.map((monthItem) => (
                      <React.Fragment key={monthItem.monthLabel}>
                        <Typography variant="body2">{monthItem.monthLabel}</Typography>
                        <Typography variant="body2">{monthItem.hours}</Typography>
                        <Typography variant="body2">{monthItem.minutes}</Typography>
                        <Typography variant="body2">
                          {new Intl.NumberFormat(undefined, {
                            style: "currency",
                            currency: "USD",
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          }).format(monthItem.amount)}
                        </Typography>
                      </React.Fragment>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}

            {summary && (
              <Grid size={{ xs: 12 }}>
                <Alert severity={summary.importedCount > 0 ? "success" : "warning"}>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Import Complete
                  </Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Imported <strong>{summary.importedCount}</strong> timesheet rows successfully across <strong>{summary.totalBatches}</strong> batch(es) in <strong>{summary.elapsedSeconds}</strong> second(s).
                  </Typography>
                  {summary.skippedCount > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {summary.skippedCount} row(s) were skipped. See details below.
                    </Typography>
                  )}
                </Alert>
              </Grid>
            )}

            {!isImporting && !summary && !isParsing && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Parse Issues ({parseIssues.length})
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                    Row label format: SheetName!RowNumber (example: Feb 2026!18 = row 18 in sheet "Feb 2026").
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, my: 0, maxHeight: 240, overflowY: "auto" }}>
                    {parseIssues.map((issue) => (
                      <li key={issue}>
                        <Typography variant="caption">{issue}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}

            {!isImporting && !summary && parseDetails && (
              <Grid size={{ xs: 12 }}>
                <Alert severity={parseDetails.totalAcceptedRows > 0 ? "info" : "warning"}>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Parse Summary
                  </Typography>
                  <Typography variant="caption" component="div">
                    Workbook sheets read: {parseDetails.totalSheets} | Total sheet rows: {parseDetails.totalSheetRows}
                  </Typography>
                  <Typography variant="caption" component="div">
                    Rows scanned: {parseDetails.totalRowsScanned} | Rows with data: {parseDetails.totalRowsWithData} | Valid rows: {parseDetails.totalAcceptedRows}
                  </Typography>
                  <Typography variant="caption" component="div">
                    Rejected rows: {parseDetails.totalRejectedRows} | Date-only rows: {parseDetails.totalDateOnlyRows} | Empty rows: {parseDetails.totalEmptyRows} | Skipped (ClockIn=ClockOut): {parseDetails.totalEqualClockInOutRows}
                  </Typography>
                  <Typography variant="caption" component="div">
                    Skipped (overlap within file): {parseDetails.totalInFileOverlapRows || 0}
                  </Typography>
                  <Typography variant="caption" component="div" sx={{ mb: 0.5 }}>
                    Parser errors: {parseDetails.totalParserErrors} | Issue messages: {parseDetails.issueCount}
                  </Typography>

                  {parseDetails.sheetSummaries.length > 0 && (
                    <Box component="ul" sx={{ pl: 2, my: 0 }}>
                      {parseDetails.sheetSummaries.slice(0, 20).map((sheet) => (
                        <li key={sheet.name}>
                          <Typography variant="caption">
                            {sheet.name}: rows={sheet.totalRows}, startRow={sheet.startRow}, header={sheet.hasRecognizedHeader ? "yes" : "no"}, scanned={sheet.rowsScanned}, data={sheet.rowsWithData}, valid={sheet.acceptedRows}, rejected={sheet.rejectedRows}, date-only={sheet.dateOnlyRows}, skipped={sheet.equalClockInOutRows}, errors={sheet.parserErrors}
                          </Typography>
                        </li>
                      ))}
                    </Box>
                  )}
                </Alert>
              </Grid>
            )}

            {summary && importIssues.length > 0 && (
              <Grid size={{ xs: 12 }}>
                <Alert severity="warning">
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>
                    Skipped During Import ({importIssues.length})
                  </Typography>
                  <Box component="ul" sx={{ pl: 2, my: 0 }}>
                    {importIssues.slice(0, 15).map((issue) => (
                      <li key={issue}>
                        <Typography variant="caption">{issue}</Typography>
                      </li>
                    ))}
                  </Box>
                </Alert>
              </Grid>
            )}

            {!isImporting && !summary && (
              <Grid size={{ xs: 12 }}>
                <Typography variant="body2" color="text.secondary">
                  Ready to import: {resolvedImportData.rowsWithRates.length} valid row(s) across {resolvedImportData.monthly.length} month(s)
                </Typography>
              </Grid>
            )}
          </Grid>
        </Box>
    </Overlay>
  );
};

export default TimesheetsImport;
